import {inject, Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, debounceTime, EMPTY, Observable, tap} from 'rxjs';
import {
  BtwOverzicht,
  BtwOverzichtFrequentie, BtwRekenService, CorporateTaxEntity,
  ErrorMessage,
  IntermediaryEntity,
  MenuInterface,
  ValueAddedTaxEntity,
} from '@snelstart-ventures/fiscaal/domain-logic';

@Injectable({providedIn: 'root'})
export class BokkzBtwDataService implements OnDestroy {

  private apiService = inject(BtwRekenService);

  public valueAddedTaxEntity = new BehaviorSubject<ValueAddedTaxEntity>(
    this.getFromSessionStorage<ValueAddedTaxEntity>('valueAddedTaxEntity') || <ValueAddedTaxEntity>{},
  );

  public options = new BehaviorSubject<MenuInterface[]>([]);
  public errorResponse = new BehaviorSubject<ErrorMessage[]>([]);
  public aantalValidaties = new BehaviorSubject<number>(0);
  public periodeIndex = new BehaviorSubject<number>(0);
  public valueAddedTaxEntity$: Observable<CorporateTaxEntity> = this.valueAddedTaxEntity.asObservable();

  public patchValueAddedTaxEntity(updater: (entity: ValueAddedTaxEntity) => ValueAddedTaxEntity): void {
    const current = this.valueAddedTaxEntity.value;
    this.valueAddedTaxEntity.next(updater(current));
  }

  calculateLatestValueAddedTaxEntity(): Observable<BtwOverzicht> {
    const aangifte = this.valueAddedTaxEntity.value.overzicht;

    if (!aangifte) {
      return EMPTY;
    }

    return this.apiService.getValueAddedTaxCalculation(aangifte).pipe(
      tap(result => {
        const response = result as unknown as {overzicht?: BtwOverzicht};
        const nextOverzicht = response.overzicht ?? (result as BtwOverzicht);

        if (!nextOverzicht) {
          return;
        }

        const current = this.valueAddedTaxEntity.value;
        this.valueAddedTaxEntity.next({
          ...current,
          overzicht: nextOverzicht
        });
      })
    );
  }

  constructor() {
    this.applyDebounceTime();
  }

  private applyDebounceTime() {
    const debounceDelay = 500;
    // this.valueAddedTaxEntity = this.valueAddedTaxEntity.pipe(
    //   debounceTime(debounceDelay),
    //   tap((value) => this.saveToSessionStorage('valueAddedTaxEntity', value))) as BehaviorSubject<ValueAddedTaxEntity>;
    this.valueAddedTaxEntity
      .pipe(
        debounceTime(debounceDelay),
        tap((value) => this.saveToSessionStorage('valueAddedTaxEntity', value)),
      )
      .subscribe();

    this.errorResponse = this.errorResponse as BehaviorSubject<ErrorMessage[]>;
    this.aantalValidaties = this.aantalValidaties as BehaviorSubject<number>;
  }

  //Voor het ophalen uit de sessionstorage.
  private getFromSessionStorage<T>(key: string): T | null {
    const value = sessionStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  }

  //Voor het opslaan in sessionstorage
  private saveToSessionStorage(key: string, value: any) {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  updateValueAddedTaxEntity(valueAddedTaxEntity: ValueAddedTaxEntity, intermediaryEntity?: IntermediaryEntity) {
    if (valueAddedTaxEntity && valueAddedTaxEntity.overzicht) {
      if (valueAddedTaxEntity.overzicht?.icpAangiftes) {
        if (intermediaryEntity) {
          if (intermediaryEntity.beconId) {
            valueAddedTaxEntity.overzicht?.icpAangiftes?.forEach((aangifte) => {
              aangifte.beconnummer = intermediaryEntity.beconId!
            });
          }
          else {
            valueAddedTaxEntity.overzicht?.icpAangiftes?.forEach((aangifte) => {
              aangifte.beconnummer = undefined;
            });
          }
        }
      }
      this.createMenuOptions(valueAddedTaxEntity.overzicht);
      this.valueAddedTaxEntity.next(valueAddedTaxEntity);
    }
  }

  private createMenuOptions(overzicht: BtwOverzicht) {
    const icpCount = overzicht.icpAangiftes?.length ?? 0;

    const options: MenuInterface[] = [
      {
        value: 0,
        id: 'belastingplichtige',
        label: 'Algemeen',
        visible: true,
        subonderdelen: [
          {
            label: 'Belastingplichtige',
            pageMapping: 'Belastingplichtige',
            visible: true,
            value: 0,
          },
          {
            label: 'Fiscale eenheid',
            pageMapping: 'Fiscale eenheid',
            visible: false,
            value: 2,
          },
        ],
      },
      {
        value: 1,
        id: 'aangiften',
        label: 'BTW aangiften',
        visible: true,
        subonderdelen: [
          {
            label: 'Eerste kwartaal',
            pageMapping: 'Eerste kwartaal',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL,
            value: 0,
          },
          {
            label: 'Tweede kwartaal',
            pageMapping: 'Tweede kwartaal',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL,
            value: 1,
          },
          {
            label: 'Derde kwartaal',
            pageMapping: 'Derde kwartaal',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL,
            value: 2,
          },
          {
            label: 'Vierde kwartaal',
            pageMapping: 'Vierde kwartaal',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.KWARTAAL,
            value: 3,
          },
          {
            label: 'Januari',
            pageMapping: 'Januari',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 0,
          },
          {
            label: 'Februari',
            pageMapping: 'Februari',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 1,
          },
          {
            label: 'Maart',
            pageMapping: 'Maart',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 2,
          },
          {
            label: 'April',
            pageMapping: 'April',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 3,
          },
          {
            label: 'Mei',
            pageMapping: 'Mei',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 4,
          },
          {
            label: 'Juni',
            pageMapping: 'Juni',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 5,
          },
          {
            label: 'Juli',
            pageMapping: 'Juli',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 6,
          },
          {
            label: 'Augustus',
            pageMapping: 'Augustus',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 7,
          },
          {
            label: 'September',
            pageMapping: 'September',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 8,
          },
          {
            label: 'Oktober',
            pageMapping: 'Oktober',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 9,
          },
          {
            label: 'November',
            pageMapping: 'November',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 10,
          },
          {
            label: 'December',
            pageMapping: 'December',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.MAAND,
            value: 11,
          },
          {
            label: 'Jaaraangifte',
            pageMapping: 'Jaaraangifte',
            visible: overzicht.frequentie === BtwOverzichtFrequentie.JAAR,
            value: 0,
          },
        ],
      },
      {
        value: 2,
        id: 'icp',
        label: 'ICP opgaves',
        visible: true,
        subonderdelen: [
          {
            label: this.getIcpLabel(0, overzicht),
            pageMapping: 'ICP opgave',
            visible: icpCount >= 1,
            value: 0,
          },
          {
            label: this.getIcpLabel(1, overzicht),
            pageMapping: 'ICP opgave',
            visible: icpCount >= 2,
            value: 1,
          },
          {
            label: this.getIcpLabel(2, overzicht),
            pageMapping: 'ICP opgave',
            visible: icpCount >= 3,
            value: 2,
          },
          {
            label: this.getIcpLabel(3, overzicht),
            pageMapping: 'ICP opgave',
            visible: icpCount >= 4,
            value: 3,
          },
          {
            label: this.getIcpLabel(4, overzicht),
            pageMapping: 'ICP opgave',
            visible: icpCount >= 5,
            value: 5,
          },
          {
            label: this.getIcpLabel(5, overzicht),
            pageMapping: 'ICP opgave',
            visible: icpCount >= 6,
            value: 5,
          },
          {
            label: this.getIcpLabel(6, overzicht),
            pageMapping: 'ICP opgave',
            visible: icpCount >= 7,
            value: 6,
          },
          {
            label: this.getIcpLabel(7, overzicht),
            pageMapping: 'ICP opgave',
            visible: icpCount >= 8,
            value: 7,
          },
          {
            label: this.getIcpLabel(8, overzicht),
            pageMapping: 'ICP opgave',
            visible: icpCount >= 9,
            value: 8,
          },
          {
            label: this.getIcpLabel(9, overzicht),
            pageMapping: 'ICP opgave',
            visible: icpCount >= 10,
            value: 9,
          },
          {
            label: this.getIcpLabel(10, overzicht),
            pageMapping: 'ICP opgave',
            visible: icpCount >= 11,
            value: 10,
          },
          {
            label: this.getIcpLabel(11, overzicht),
            pageMapping: 'ICP opgave',
            visible: icpCount >= 12,
            value: 11,
          },
        ],
      },
      {
        value: 3,
        id: 'suppleties',
        label: 'Suppleties',
        visible: true,
        subonderdelen: [
          {
            label:
              'Suppletie ' +
              new Date(overzicht.btwSuppleties?.at(0)?.tijdvak?.begindatum!).toLocaleDateString('nl-NL') +
              ' tot en met ' +
              new Date(overzicht.btwSuppleties?.at(0)?.tijdvak?.einddatum!).toLocaleDateString('nl-NL') || 'Suppletie',
            pageMapping: 'Suppletie',
            visible: overzicht.btwSuppleties?.at(0)?.tijdvak?.begindatum !== undefined,
            value: 0,
          },
          {
            label:
              'Suppletie ' +
              new Date(overzicht.btwSuppleties?.at(1)?.tijdvak?.begindatum!).toLocaleDateString('nl-NL') +
              ' tot en met ' +
              new Date(overzicht.btwSuppleties?.at(1)?.tijdvak?.einddatum!).toLocaleDateString('nl-NL') || 'Suppletie',
            pageMapping: 'Suppletie',
            visible: overzicht.btwSuppleties?.at(1)?.tijdvak?.begindatum !== undefined,
            value: 1,
          },
          {
            label:
              'Suppletie ' +
              new Date(overzicht.btwSuppleties?.at(2)?.tijdvak?.begindatum!).toLocaleDateString('nl-NL') +
              ' tot en met ' +
              new Date(overzicht.btwSuppleties?.at(2)?.tijdvak?.einddatum!).toLocaleDateString('nl-NL') || 'Suppletie',
            pageMapping: 'Suppletie',
            visible: overzicht.btwSuppleties?.at(2)?.tijdvak?.begindatum !== undefined,
            value: 2,
          },
          {
            label:
              'Suppletie ' +
              new Date(overzicht.btwSuppleties?.at(3)?.tijdvak?.begindatum!).toLocaleDateString('nl-NL') +
              ' tot en met ' +
              new Date(overzicht.btwSuppleties?.at(3)?.tijdvak?.einddatum!).toLocaleDateString('nl-NL') || 'Suppletie',
            pageMapping: 'Suppletie',
            visible: overzicht.btwSuppleties?.at(3)?.tijdvak?.begindatum !== undefined,
            value: 3,
          },
          {
            label:
              'Suppletie ' +
              new Date(overzicht.btwSuppleties?.at(4)?.tijdvak?.begindatum!).toLocaleDateString('nl-NL') +
              ' tot en met ' +
              new Date(overzicht.btwSuppleties?.at(4)?.tijdvak?.einddatum!).toLocaleDateString('nl-NL') || 'Suppletie',
            pageMapping: 'Suppletie',
            visible: overzicht.btwSuppleties?.at(4)?.tijdvak?.begindatum !== undefined,
            value: 4,
          },
          {
            label:
              'Suppletie ' +
              new Date(overzicht.btwSuppleties?.at(5)?.tijdvak?.begindatum!).toLocaleDateString('nl-NL') +
              ' tot en met ' +
              new Date(overzicht.btwSuppleties?.at(5)?.tijdvak?.einddatum!).toLocaleDateString('nl-NL') || 'Suppletie',
            pageMapping: 'Suppletie',
            visible: overzicht.btwSuppleties?.at(5)?.tijdvak?.begindatum !== undefined,
            value: 5,
          },
          {
            label:
              'Suppletie ' +
              new Date(overzicht.btwSuppleties?.at(6)?.tijdvak?.begindatum!).toLocaleDateString('nl-NL') +
              ' tot en met ' +
              new Date(overzicht.btwSuppleties?.at(6)?.tijdvak?.einddatum!).toLocaleDateString('nl-NL') || 'Suppletie',
            pageMapping: 'Suppletie',
            visible: overzicht.btwSuppleties?.at(6)?.tijdvak?.begindatum !== undefined,
            value: 6,
          },
          {
            label:
              'Suppletie ' +
              new Date(overzicht.btwSuppleties?.at(7)?.tijdvak?.begindatum!).toLocaleDateString('nl-NL') +
              ' tot en met ' +
              new Date(overzicht.btwSuppleties?.at(7)?.tijdvak?.einddatum!).toLocaleDateString('nl-NL') || 'Suppletie',
            pageMapping: 'Suppletie',
            visible: overzicht.btwSuppleties?.at(7)?.tijdvak?.begindatum !== undefined,
            value: 7,
          },
          {
            label:
              'Suppletie ' +
              new Date(overzicht.btwSuppleties?.at(8)?.tijdvak?.begindatum!).toLocaleDateString('nl-NL') +
              ' tot en met ' +
              new Date(overzicht.btwSuppleties?.at(8)?.tijdvak?.einddatum!).toLocaleDateString('nl-NL') || 'Suppletie',
            pageMapping: 'Suppletie',
            visible: overzicht.btwSuppleties?.at(8)?.tijdvak?.begindatum !== undefined,
            value: 8,
          },
          {
            label:
              'Suppletie ' +
              new Date(overzicht.btwSuppleties?.at(9)?.tijdvak?.begindatum!).toLocaleDateString('nl-NL') +
              ' tot en met ' +
              new Date(overzicht.btwSuppleties?.at(9)?.tijdvak?.einddatum!).toLocaleDateString('nl-NL') || 'Suppletie',
            pageMapping: 'Suppletie',
            visible: overzicht.btwSuppleties?.at(9)?.tijdvak?.begindatum !== undefined,
            value: 9,
          },
          {
            label:
              'Suppletie ' +
              new Date(overzicht.btwSuppleties?.at(10)?.tijdvak?.begindatum!).toLocaleDateString('nl-NL') +
              ' tot en met ' +
              new Date(overzicht.btwSuppleties?.at(10)?.tijdvak?.einddatum!).toLocaleDateString('nl-NL') || 'Suppletie',
            pageMapping: 'Suppletie',
            visible: overzicht.btwSuppleties?.at(10)?.tijdvak?.begindatum !== undefined,
            value: 10,
          },
          {
            label:
              'Suppletie ' +
              new Date(overzicht.btwSuppleties?.at(11)?.tijdvak?.begindatum!).toLocaleDateString('nl-NL') +
              ' tot en met ' +
              new Date(overzicht.btwSuppleties?.at(11)?.tijdvak?.einddatum!).toLocaleDateString('nl-NL') || 'Suppletie',
            pageMapping: 'Suppletie',
            visible: overzicht.btwSuppleties?.at(11)?.tijdvak?.begindatum !== undefined,
            value: 11,
          },
        ],
      },
      {
        value: 4,
        id: 'overzicht',
        label: 'Overzicht',
        visible: true,
        subonderdelen: [
          {
            label: 'Rondrekening',
            pageMapping: 'Rondrekening',
            visible: true,
            value: 0,
          },
        ],
      },
    ];
    this.options.next(options);
  }

  getIcpLabel(index: number, overzicht: BtwOverzicht): string {
    const aangifte = overzicht?.icpAangiftes?.[index];
    const tijdvak = aangifte?.tijdvak;

    if (tijdvak?.begindatum && tijdvak?.einddatum) {
      const start = new Date(tijdvak.begindatum).toLocaleDateString('nl-NL');
      const end = new Date(tijdvak.einddatum).toLocaleDateString('nl-NL');
      return `ICP opgave ${start} tot en met ${end}`;
    }

    return 'ICP opgave';
  }

  updateErrorResponse(errorResponse: ErrorMessage[]) {
    this.errorResponse.next(errorResponse);
  }

  updateAantalValidaties(aantalValidaties: number) {
    this.aantalValidaties.next(aantalValidaties);
  }

  updatePeriodeIndex(periodeIndex: number) {
    this.periodeIndex.next(periodeIndex);
  }

  ngOnDestroy(): void {
    this.valueAddedTaxEntity.complete();
    if (this.options) {
      this.options?.unsubscribe();
    }
    if (this.errorResponse) {
      this.errorResponse?.unsubscribe();
    }
    if (this.aantalValidaties) {
      this.aantalValidaties?.unsubscribe();
    }
    if (this.periodeIndex) {
      this.periodeIndex.unsubscribe();
    }
  }
}
